// src/prototype/hooks/useExecutionRecordState.ts
// ============================================================
// MAINTAINED EXECUTION RECORD — the Command Center models the product
//
// The Command Center used to render a frozen snapshot: milestone percentages, the "X of N
// complete" tile and the checklist were all read straight from the fixture, so nothing the
// presenter did on the page changed anything. This store makes the record MAINTAINED — the
// owner updates a milestone, and every derived number on the page moves with it.
//
// WHAT IS AND IS NOT MODELLED. Updating a status writes the status. It does not notify anyone,
// sync to a project tool, or trigger a workflow — there is no fake automation and no
// integration toast, because the honest claim is "this is the record of what's happening",
// not "this is wired to your PMO". The one thing that DOES cascade is a real dependency the
// fixture already declares: a blocked milestone's warning clears when its blocker completes.
//
// Overlays only. The fixture stays the canonical snapshot; this stores the presenter's
// deltas on top, keyed by initiative id. Ctrl/Cmd+Shift+D drops the overlay and the canonical
// snapshot returns exactly — 2-of-6 milestones, Design at 60%, checklist 6-of-7, Pilot Launch
// warning present.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import type { ExecMilestone, ChecklistItem } from '../data/executionInitiatives';
import type { MilestoneStatus } from '../theme/decisionTokens';

export const EXEC_RECORD_KEY = 'dva_demo_execution_record';
export const EXEC_RECORD_CHANGE_EVENT = 'dva_demo_execution_record_change';

/** A presenter edit to one milestone, stored by its title (stable within an initiative). */
export interface MilestoneOverride {
  status: MilestoneStatus;
  pct: number;
  /** ISO stamp of the edit; drives the "Updated just now" line. */
  updatedAt: string;
  updatedBy: string;
}

interface RecordOverlay {
  milestones: Record<string, MilestoneOverride>;
  /** Checklist item label → done. Absent = use the fixture value. */
  checklist: Record<string, boolean>;
}

type OverlayState = Record<string, RecordOverlay>; // initiativeId → overlay

const EMPTY_OVERLAY: RecordOverlay = { milestones: {}, checklist: {} };

/** Who the demo attributes edits to. The command centre's own owner label. */
export const EXEC_EDITOR = 'Initiative Owner';

/** The quick-pick percentages offered for In Progress. */
export const IN_PROGRESS_PICKS = [25, 50, 75] as const;

/** Canonical pct for a status. In Progress keeps whatever pick was made. */
export function pctForStatus(status: MilestoneStatus, pick?: number): number {
  if (status === 'Complete') return 100;
  if (status === 'Not Started') return 0;
  return pick ?? 50;
}

function readState(): OverlayState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(EXEC_RECORD_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as OverlayState) : {};
  } catch {
    return {};
  }
}

function writeState(next: OverlayState): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(EXEC_RECORD_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(EXEC_RECORD_CHANGE_EVENT, { detail: next }));
}

export interface ExecutionRecordApi {
  /** Fixture milestones with any presenter edits applied, in fixture order. */
  milestones: ExecMilestone[];
  /** Fixture checklist with any presenter edits applied. */
  checklist: ChecklistItem[];
  /** Derived — never read from the fixture. */
  milestonesComplete: number;
  checklistComplete: number;
  /** Title → override, for the "Updated just now" stamp. */
  overrides: Record<string, MilestoneOverride>;
  setMilestoneStatus: (title: string, status: MilestoneStatus, pick?: number) => void;
  toggleChecklistItem: (label: string) => void;
  /** Drop this initiative's overlay — back to the canonical snapshot. */
  resetRecord: () => void;
}

/**
 * A milestone's blocker is suppressed once the milestone it names has completed.
 *
 * The fixture declares the dependency in prose ("Awaiting completion of workflow design
 * milestone"), so the match is by keyword against the blocking milestone's phase/title rather
 * than by a formal edge — there is no dependency graph in the data to walk. Deliberately
 * conservative: an unrecognised blocker string is left alone rather than cleared.
 */
export function blockerCleared(blocker: string | undefined, milestones: ExecMilestone[]): boolean {
  if (!blocker) return false;
  const b = blocker.toLowerCase();
  return milestones.some((m) => {
    if (m.status !== 'Complete') return false;
    const phase = (m.phase || '').toLowerCase();
    // Match on PHASE ONLY. "…awaiting completion of workflow design milestone" names the
    // Design-phase milestone.
    //
    // An earlier version also matched long words from the milestone TITLE, which was too
    // loose in exactly the way that matters: "Current-State **Workflow** Assessment" is
    // Complete in the opening snapshot, and "workflow" appears in the blocker text — so the
    // Pilot warning cleared before the presenter had done anything. Phase names are the
    // controlled vocabulary here (Activation / Discovery / Design / Build / Pilot / Scale);
    // title words are prose and will collide.
    return phase.length >= 4 && b.includes(phase);
  });
}

export function useExecutionRecordState(
  initiativeId: string,
  fixtureMilestones: ExecMilestone[],
  fixtureChecklist: ChecklistItem[],
): ExecutionRecordApi {
  const [state, setState] = useState<OverlayState>(() => readState());

  useEffect(() => {
    const handle = (e: Event) =>
      setState((e as CustomEvent<OverlayState>).detail ?? readState());
    const onReset = () => setState(readState());
    window.addEventListener(EXEC_RECORD_CHANGE_EVENT, handle as EventListener);
    window.addEventListener('ea_demo_reset', onReset);
    return () => {
      window.removeEventListener(EXEC_RECORD_CHANGE_EVENT, handle as EventListener);
      window.removeEventListener('ea_demo_reset', onReset);
    };
  }, []);

  const overlay = state[initiativeId] ?? EMPTY_OVERLAY;

  const milestones: ExecMilestone[] = fixtureMilestones.map((m) => {
    const o = overlay.milestones?.[m.title];
    return o ? { ...m, status: o.status, pct: o.pct } : m;
  });

  const checklist: ChecklistItem[] = fixtureChecklist.map((c) => {
    const o = overlay.checklist?.[c.label];
    return o === undefined ? c : { ...c, done: o };
  });

  const setMilestoneStatus = useCallback(
    (title: string, status: MilestoneStatus, pick?: number) => {
      const cur = readState();
      const prev = cur[initiativeId] ?? EMPTY_OVERLAY;
      const next: OverlayState = {
        ...cur,
        [initiativeId]: {
          ...prev,
          milestones: {
            ...prev.milestones,
            [title]: {
              status,
              pct: pctForStatus(status, pick),
              updatedAt: new Date().toISOString(),
              updatedBy: EXEC_EDITOR,
            },
          },
        },
      };
      writeState(next);
      setState(next);
    },
    [initiativeId],
  );

  const toggleChecklistItem = useCallback(
    (label: string) => {
      const cur = readState();
      const prev = cur[initiativeId] ?? EMPTY_OVERLAY;
      const fixtureValue = fixtureChecklist.find((c) => c.label === label)?.done ?? false;
      const current = prev.checklist?.[label] ?? fixtureValue;
      const next: OverlayState = {
        ...cur,
        [initiativeId]: { ...prev, checklist: { ...prev.checklist, [label]: !current } },
      };
      writeState(next);
      setState(next);
    },
    [initiativeId, fixtureChecklist],
  );

  const resetRecord = useCallback(() => {
    const cur = readState();
    const next = { ...cur };
    delete next[initiativeId];
    writeState(next);
    setState(next);
  }, [initiativeId]);

  return {
    milestones,
    checklist,
    milestonesComplete: milestones.filter((m) => m.status === 'Complete').length,
    checklistComplete: checklist.filter((c) => c.done).length,
    overrides: overlay.milestones ?? {},
    setMilestoneStatus,
    toggleChecklistItem,
    resetRecord,
  };
}

export default useExecutionRecordState;
